// @dynamic-i18n-keys
// Dynamic t() at lines 89-92: template mediapopup.searchForDeviations${allowSearchByUrl ? '.allowSearchByUrl' : ''}.
//   mediapopup.searchForDeviations
//   mediapopup.searchForDeviations.allowSearchByUrl
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { useCurrentMediaSource } from '../../_hocs/withCurrentMediaSource';
import { useSearch } from '../../_hocs/withSearch';
import { MediaSourceType } from '../../types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import s from './SearchInput.scss';

export interface Props {
  className?: string;
  /** if true, will attempt to parse input contents as a deviation url and
   *  look up with deviation/fetch */
  allowSearchByUrl?: boolean;
}

const SearchInput: React.FC<Props> = ({ className, allowSearchByUrl }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { currentMediaSource } = useCurrentMediaSource();
  const { searchInput, setSearchInput, setSearchQuery, unsetSearchQuery } =
    useSearch();

  const [isFocused, setIsFocused] = useState(false);
  const inputElRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (searchInput && inputElRef.current) {
      inputElRef.current.focus();
    }
  }, [searchInput]);

  useEffect(() => {
    if (isFocused && inputElRef.current) {
      inputElRef.current.focus();
    }
  }, [isFocused]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (inputElRef.current) {
      handleSearch(inputElRef.current.value);
    }
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    // Timeout allows us to dismiss the action if blur happened due to "clear" being clicked
    if (isMobile) {
      blurTimeoutRef.current = window.setTimeout(() => {
        handleSearch(searchInput);
      }, 100);
    }

    setIsFocused(false);
  };

  const handleSearch = (searchInputValue?: string) => {
    if (searchInputValue) {
      setSearchQuery(searchInputValue, allowSearchByUrl);
    } else {
      unsetSearchQuery();
    }
  };

  if (currentMediaSource === MediaSourceType.STASH) {
    return null; // stash doesn't support searching
  }

  const isActive = isFocused || searchInput;
  const label = t(
    `mediapopup.searchForDeviations${
      allowSearchByUrl ? '.allowSearchByUrl' : ''
    }`
  );

  return (
    <div className={classnames(s['root'], isActive && s['active'], className)}>
      {(!isMobile || isActive) && (
        <TextInput
          inputClassname={s['input']}
          ref={inputElRef}
          id="search-input"
          label={label}
          placeholder={label}
          value={searchInput}
          onChange={onChange}
          onKeyPress={onKeyPress}
          onBlur={onBlur}
          onFocus={onFocus}
          postfix={
            <PlainButtonOrLink onClick={handleSubmit}>
              <SearchIcon disableDefaultColors size={IconSize.SMALLER} />
            </PlainButtonOrLink>
          }
          noBottomSpacing
          isLabelHidden
        />
      )}
    </div>
  );
};

export default SearchInput;
